'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Cookies from 'js-cookie';
import { api } from '@/lib/apiClient';
import { useRouter } from 'next/navigation';

export default function LoginPage() {
    const router = useRouter();

    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState(['', '', '', '']);
    const [otpResponse, setOtpResponse] = useState('');
    const [showOtp, setShowOtp] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [countdown, setCountdown] = useState(0);

    const otpRefs = useRef([]);
    const timerRef = useRef(null);

    // Cleanup timer when component unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    // Handle OTP change
    const handleOtpChange = (value, index) => {
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        // Move forward
        if (value && index < 3) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    // Handle Backspace
    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    // Start countdown
    const startCountdown = () => {
        setCountdown(60);

        timerRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const handleGetOtp = async () => {
        if (mobile.length !== 10) {
            alert('Enter valid mobile number');
            return;
        }

        setIsLoading(true);

        try {
            const response = await api.post('getlogin', { mobile });

            if (response.success === 1) {
                console.log("OTP:", response.otp);


                setOtpResponse(response.otp);
                setShowOtp(true);
                setOtp(['', '', '', '']);
                startCountdown();

                // Auto focus first OTP box
                setTimeout(() => {
                    otpRefs.current[0]?.focus();
                }, 100);

            } else {
                alert(response.msg);
            }
        } catch (error) {
            console.log(error);
            alert("Something went wrong");
        }

        setIsLoading(false);
    };

    const handleLogin = async () => {
        const enteredOtp = otp.join('');

        if (enteredOtp.length !== 4) {
            alert('Enter valid OTP');
            return;
        }

        if (otpResponse !== enteredOtp) {
            alert('Please enter correct OTP');
            return;
        }

        setIsLoading(true);

        try {
            const response = await api.post('checklogin', { mobile });

            if (response.success === 1) {
                const expiresIn = response.expiresIn || 3600;

                const cookieOptions = {
                    secure: process.env.NODE_ENV === 'production',
                    sameSite: 'strict',
                    expires: new Date(Date.now() + expiresIn * 1000),
                    path: '/',
                };

                Cookies.set('token', response.token, cookieOptions);
                Cookies.set('name', JSON.stringify(response.name), cookieOptions);
                Cookies.set('id', JSON.stringify(response.id), cookieOptions);
                Cookies.set('mobile', JSON.stringify(response.mobile), cookieOptions);
                Cookies.set('role', JSON.stringify(response.userrole), cookieOptions);

                const searchParams = new URLSearchParams(window.location.search);
                const redirectPath = searchParams.get('redirect');

                if (redirectPath) {
                    router.push(redirectPath);
                } else if (['seller', 'agent'].includes(response.userrole)) {
                    router.push('/seller/dashboard');
                } else if (['builder'].includes(response.userrole)) {
                    router.push('/seller/projects');
                } else {
                    router.push('/my/activity');
                }
            }
        } catch (error) {
            console.error(error);
            alert("Login failed");
        }

        setIsLoading(false);
    };

    return (
        <div className="auth-page">
            <div className="auth-card">
                <Link
                    href="/"
                    className="inline-flex lg:hidden items-center gap-2 px-4 py-2 rounded-lg bg-gray-200 back-to text-white hover:bg-primary-600 transition-all duration-200"
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>

                    <span>Back to Home</span>
                </Link>
                <div className="auth-header">
                    <Link href="/" className="logo inline-flex items-center gap-2">
                        <Image
                            src="/da-logo.png" // public/images/logo.png
                            alt="Dream Acres Logo"
                            width={260}
                            height={50}
                            priority
                        />
                    </Link>
                    <h1>Login with OTP</h1>
                    <p>Enter your mobile number to continue</p>
                </div>

                <div className="auth-form">

                    {/* Mobile + OTP Button / Countdown */}
                    <div className="form-group">
                        <label>Mobile Number</label>
                        <div className="flex gap-2 items-center">
                            <input
                                type="tel"
                                value={mobile}
                                readOnly={showOtp && countdown > 0}
                                onChange={(e) => setMobile(e.target.value)}
                                placeholder="Enter mobile number"
                                className={`input flex-1 ${showOtp && countdown > 0
                                    ? 'bg-gray-100 cursor-not-allowed'
                                    : ''
                                    }`}
                                maxLength={10}
                            />

                            {countdown > 0 ? (
                                <div className="px-4 py-2 bg-gray-200 rounded-md text-sm font-medium min-w-[80px] text-center">
                                    00:{countdown.toString().padStart(2, '0')}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={handleGetOtp}
                                    className="btn btn-primary"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Sending...' : showOtp ? 'Resend OTP' : 'Get OTP'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* OTP Inputs */}
                    {showOtp && (
                        <div className="form-group mt-6">
                            <label>Enter OTP</label>
                            <div className="flex gap-3 justify-center mt-2">
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        maxLength="1"
                                        value={digit}
                                        ref={el => otpRefs.current[index] = el}
                                        onChange={(e) => handleOtpChange(e.target.value, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        className="w-12 h-12 text-center border rounded-lg text-lg font-semibold focus:outline-primary"
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Login Button */}
                    {showOtp && otp.join('').length === 4 && (
                        <button
                            onClick={handleLogin}
                            className="btn btn-primary w-full mt-6"
                            disabled={isLoading}
                        >
                            {isLoading ? 'Verifying...' : 'Login'}
                        </button>
                    )}

                </div>

                <p className="text-center text-gray-600 text-sm mt-6">
                    Don't have an account?{' '}
                    <Link href="/register" className="text-primary font-medium hover:underline">
                        Create Account
                    </Link>
                </p>


            </div>
        </div>
    );
}
